import { get, type Writable } from "svelte/store";
import { BundleType, ConversionType, DetailItemSchema, type DetailItem, type Suite } from "#gen/bundles/v1/bundle_pb.js";
import { CommerceDataSchema, type CommerceData } from "#gen/bundles/v1/commerce_pb.js";
import { ImageSchema, type Image } from "#gen/bundles/v1/common_pb.js";
import { ContactDataSchema, type ContactData } from "#gen/bundles/v1/contact_pb.js";
import { FormDataSchema, type FormData } from "#gen/bundles/v1/form_pb.js";
import { RealEstateDataSchema, type RealEstateData } from "#gen/bundles/v1/real_estate_pb.js";
import { formattedNeighborhood, formattedStreetAddress } from "#lib/address.js";
import type { AssetLoad } from "#lib/api/bundles.js";
import { formatPrice, moneyAsNumber } from "#lib/money.js";
import { formatNumber } from "#lib/number.js";
import { create } from "#lib/protobuf-compat.js";

type DataType = RealEstateData | CommerceData | FormData | ContactData | undefined;

export const DEFAULT_COLOR = "#374151";
const HIGHLIGHT_COLOR = "#00A63E";
const WARN_COLOR = "#E7000A";

export interface DataItem<T extends DataType> {
	primaryText: string;
	secondaryText?: DetailItem;
	tertiaryText: DetailItem[];
	supportingText?: string;
	images: Image[];
	successHeader: string;
	successText: string;
	highlightText?: DetailItem;
	ctaText?: string;
	canConvert: boolean;
	suiteId: string;
	value: T;
}

export const FALLBACK_ACTION_LABEL = "Get Started";

const actionLabels: Map<ConversionType, string> = new Map([
	[ConversionType.CONTACT_COLLECTION, "Contact"],
	[ConversionType.PURCHASE, "Purchase"],
]);
export const defaultActionLabels: Map<BundleType, string> = new Map([
	[BundleType.REAL_ESTATE, "Contact"],
	[BundleType.COMMERCE, "Purchase"],
	[BundleType.CONTACT, "Add to contacts"],
	[BundleType.FORM, "Get started"],
]);

export interface SuiteStore<T extends DataType = DataType> extends Writable<Suite> {
	data: Writable<DataItem<T>>;
}

export function createDataItem(suite: Suite, asset?: AssetLoad): DataItem<DataType> {
	let dataItem: DataItem<DataType>;
	switch (suite.bundleType) {
		case BundleType.COMMERCE:
			dataItem = createCommerceDataStore(suite, asset);
			break;
		case BundleType.CONTACT:
			dataItem = createContactDataStore(suite, asset);
			break;
		case BundleType.EXTERNAL:
			dataItem = createExternalDataStore(suite, asset);
			break;
		case BundleType.FORM:
			dataItem = createFormDataStore(suite, asset);
			break;
		case BundleType.REAL_ESTATE:
			dataItem = createRealEstateDataStore(suite, asset);
			break;
		case BundleType.CUSTOM:
			dataItem = createCustomDataStore(suite, asset);
			break;
		default:
			throw Error("invalid case");
	}
	if (asset?.asLandingPage()) {
		return createLandingPageDataStore(suite, asset, dataItem);
	}
	return dataItem;
}

export function createRealEstateDataStore(suite: Suite, asset?: AssetLoad): DataItem<RealEstateData> {
	const data = suite.data?.type.case === "realEstate" ? suite.data.type.value : create(RealEstateDataSchema);

	function getPrimaryText(data: RealEstateData): string {
		return formattedStreetAddress(data.listing?.address) ?? "";
	}

	function getSecondaryText(data: RealEstateData): DetailItem | undefined {
		const neighborhood = formattedNeighborhood(data.listing?.address);
		if (neighborhood.length === 0) {
			return undefined;
		}
		return create(DetailItemSchema, {
			iconSet: "heroicons",
			icon: "map-pin",
			text: neighborhood,
		});
	}

	function getTertiaryText(data: RealEstateData): DetailItem[] {
		if (!data.listing) {
			return [];
		}
		const items: DetailItem[] = [
			create(DetailItemSchema, {
				iconSet: "heroicons",
				icon: "tag",
				text: formatPrice(data?.listing?.price, "USD", { maximumFractionDigits: 0 }),
				colorHex: DEFAULT_COLOR,
			}),
			create(DetailItemSchema, {
				iconSet: "material-symbols",
				icon: "bed-outline",
				text: `${data.listing?.bedrooms ?? 0} bed${(data.listing?.bedrooms ?? 0) > 1 ? "s" : ""}`,
				colorHex: DEFAULT_COLOR,
			}),
		];
		if ((data.listing?.halfBathrooms ?? 0) === 0) {
			items.push(
				create(DetailItemSchema, {
					iconSet: "mdi",
					icon: "bathtub-outline",
					text: `${data.listing?.fullBathrooms ?? 0} bath${(data.listing?.fullBathrooms ?? 0) === 1 ? "" : "s"}`,
					colorHex: DEFAULT_COLOR,
				}),
			);
		} else if ((data.listing?.halfBathrooms ?? 0) === 1) {
			items.push(
				create(DetailItemSchema, {
					iconSet: "mdi",
					icon: "bathtub-outline",
					text: `${data.listing?.fullBathrooms ?? 0}.5 baths`,
					colorHex: DEFAULT_COLOR,
				}),
			);
		} else {
			items.push(
				create(DetailItemSchema, {
					iconSet: "mdi",
					icon: "bathtub-outline",
					text: `${data.listing?.fullBathrooms ?? 0} baths`,
					colorHex: DEFAULT_COLOR,
				}),
				create(DetailItemSchema, {
					iconSet: "mdi",
					icon: "bathtub-outline",
					text: `${data.listing?.halfBathrooms ?? 0} half baths`,
					colorHex: DEFAULT_COLOR,
				}),
			);
		}
		items.push(
			create(DetailItemSchema, {
				iconSet: "material-symbols",
				icon: "square-foot",
				text: `${data.listing?.livingArea ?? 0} sq/ft`,
				colorHex: DEFAULT_COLOR,
			}),
		);
		return items;
	}

	function getImages(data: RealEstateData): Image[] {
		return data.listing?.photos?.map(value => create(ImageSchema, { url: value.replace("http://", "https://") })) ?? [];
	}

	function getSuccessHeader(_data: RealEstateData): string {
		return "Thank you!";
	}

	function getSuccessText(data: RealEstateData): string {
		return `${data.agent?.firstName} will contact you shortly`;
	}

	function getHighlightText(_data: RealEstateData): DetailItem | undefined {
		return undefined;
	}

	function getCanConvert(data: RealEstateData): boolean {
		return data.listing ? true : false;
	}

	function getCtaText(suite: Suite, key: string, _data: RealEstateData): string | undefined {
		return actionLabels.get(suite.assets[key]?.conversionType) ?? defaultActionLabels.get(BundleType.REAL_ESTATE);
	}

	return {
		get primaryText() {
			return getPrimaryText(data);
		},
		get secondaryText() {
			return getSecondaryText(data);
		},
		get tertiaryText() {
			return getTertiaryText(data);
		},
		get highlightText() {
			return getHighlightText(data);
		},
		get images() {
			return getImages(data);
		},
		get successHeader() {
			return getSuccessHeader(data);
		},
		get successText() {
			return getSuccessText(data);
		},
		get canConvert() {
			return getCanConvert(data);
		},
		get ctaText() {
			return getCtaText(suite, asset?.key() ?? "", data);
		},
		suiteId: suite.id,
		value: data,
	};
}

export function createCommerceDataStore(suite: Suite, asset?: AssetLoad): DataItem<CommerceData> {
	const data = suite.data?.type.case === "commerce" ? suite.data.type.value : create(CommerceDataSchema);

	function getPrimaryText(data: CommerceData): string {
		return data.product?.title ?? "";
	}

	function getSecondaryText(_data: CommerceData): DetailItem | undefined {
		return undefined;
	}

	function getTertiaryText(data: CommerceData): DetailItem[] {
		return [
			create(DetailItemSchema, {
				iconSet: "heroicons",
				icon: "tag",
				text: formatPrice(data.product?.priceRange?.minVariantPrice),
				colorHex: DEFAULT_COLOR,
			}),
		];
	}

	function getImages(data: CommerceData): Image[] {
		return data.product?.images?.map(({ url, altText }) => create(ImageSchema, { url: url.replace("http://", "https://"), altText: altText })) ?? [];
	}

	function getSuccessHeader(_data: CommerceData): string {
		return "";
	}

	function getSuccessText(_data: CommerceData): string {
		return "";
	}

	function getHighlightText(data: CommerceData): DetailItem | undefined {
		if (!data.product?.availableForSale) {
			return create(DetailItemSchema, {
				text: "Not available",
				colorHex: WARN_COLOR,
			});
		}
		if (data.product?.priceRange?.minVariantPrice && data.product?.compareAtPriceRange?.minVariantPrice?.units) {
			const regularPrice = moneyAsNumber(data.product?.compareAtPriceRange?.minVariantPrice);
			const salePrice = moneyAsNumber(data.product?.priceRange?.minVariantPrice);
			return create(DetailItemSchema, {
				text: `${formatNumber(Math.round((1 - salePrice / regularPrice) * 100))}% off`,
				colorHex: HIGHLIGHT_COLOR,
			});
		}
		if (data.product?.totalInventory && Number(data.product.totalInventory) > 0 && Number(data.product.totalInventory) <= 10) {
			return create(DetailItemSchema, {
				text: `Only ${Number(data.product?.totalInventory)} left`,
				colorHex: HIGHLIGHT_COLOR,
			});
		}
		return undefined;
	}

	function getCanConvert(data: CommerceData): boolean {
		return data.product?.availableForSale ?? false;
	}

	function getCtaText(suite: Suite, key: string, _data: CommerceData): string | undefined {
		return actionLabels.get(suite.assets[key]?.conversionType) ?? defaultActionLabels.get(BundleType.COMMERCE);
	}

	return {
		get primaryText() {
			return getPrimaryText(data);
		},
		get secondaryText() {
			return getSecondaryText(data);
		},
		get tertiaryText() {
			return getTertiaryText(data);
		},
		get highlightText() {
			return getHighlightText(data);
		},
		get images() {
			return getImages(data);
		},
		get successHeader() {
			return getSuccessHeader(data);
		},
		get successText() {
			return getSuccessText(data);
		},
		get canConvert() {
			return getCanConvert(data);
		},
		get ctaText() {
			return getCtaText(suite, asset?.key() ?? "", data);
		},
		suiteId: suite.id,
		value: data,
	};
}

export function createFormDataStore(suite: Suite, asset?: AssetLoad): DataItem<FormData> {
	const data = suite.data?.type.case === "form" ? suite.data.type.value : create(FormDataSchema);

	function getPrimaryText(data: FormData): string {
		return data.name ?? "";
	}

	function getSecondaryText(data: FormData): DetailItem | undefined {
		if (!data.description) {
			return undefined;
		}
		return create(DetailItemSchema, {
			text: data.description,
		});
	}

	function getTertiaryText(_data: FormData): DetailItem[] {
		return [];
	}

	function getImages(data: FormData): Image[] {
		data.images?.forEach(value => (value.url = value.url.replace("http://", "https://")));
		return data.images ?? [];
	}

	function getSuccessHeader(_data: FormData): string {
		return "Thank you!";
	}

	function getSuccessText(_data: FormData): string {
		return "Your submission has been submitted for review.";
	}

	function getHighlightText(_data: FormData): DetailItem | undefined {
		return undefined;
	}

	function getCanConvert(_data: FormData): boolean {
		return true;
	}

	function getCtaText(suite: Suite, key: string, _data: FormData): string | undefined {
		return actionLabels.get(suite.assets[key]?.conversionType) || defaultActionLabels.get(BundleType.FORM);
	}
	return {
		get primaryText() {
			return getPrimaryText(data);
		},
		get secondaryText() {
			return getSecondaryText(data);
		},
		get tertiaryText() {
			return getTertiaryText(data);
		},
		get highlightText() {
			return getHighlightText(data);
		},
		get images() {
			return getImages(data);
		},
		get successHeader() {
			return getSuccessHeader(data);
		},
		get successText() {
			return getSuccessText(data);
		},
		get canConvert() {
			return getCanConvert(data);
		},
		get ctaText() {
			return getCtaText(suite, asset?.key() ?? "", data);
		},
		suiteId: suite.id,
		value: data,
	};
}

export function createContactDataStore(suite: Suite, asset?: AssetLoad): DataItem<ContactData> {
	const data = suite.data?.type.case === "contact" ? suite.data.type.value : create(ContactDataSchema);

	function getPrimaryText(data: ContactData): string {
		return data.fullName ?? "";
	}

	function getSecondaryText(_data: ContactData): DetailItem | undefined {
		if (!data.title) {
			return undefined;
		}
		return create(DetailItemSchema, {
			text: data.title,
		});
	}

	function getTertiaryText(data: ContactData): DetailItem[] {
		return [
			create(DetailItemSchema, {
				text: data.company ?? "",
			}),
		];
	}

	function getImages(data: ContactData): Image[] {
		return data.photo ? [data.photo] : [];
	}

	function getSuccessHeader(_data: ContactData): string {
		return "";
	}

	function getSuccessText(_data: ContactData): string {
		return "";
	}

	function getHighlightText(_data: ContactData): DetailItem | undefined {
		return undefined;
	}

	function getCanConvert(_data: ContactData): boolean {
		return true;
	}

	function getCtaText(suite: Suite, key: string, _data: ContactData): string | undefined {
		return actionLabels.get(suite.assets[key]?.conversionType) ?? defaultActionLabels.get(BundleType.COMMERCE);
	}

	return {
		get primaryText() {
			return getPrimaryText(data);
		},
		get secondaryText() {
			return getSecondaryText(data);
		},
		get tertiaryText() {
			return getTertiaryText(data);
		},
		get highlightText() {
			return getHighlightText(data);
		},
		get images() {
			return getImages(data);
		},
		get successHeader() {
			return getSuccessHeader(data);
		},
		get successText() {
			return getSuccessText(data);
		},
		get canConvert() {
			return getCanConvert(data);
		},
		get ctaText() {
			return getCtaText(suite, asset?.key() ?? "", data);
		},
		suiteId: suite.id,
		value: data,
	};
}

function createLandingPageDataStore(suite: Suite, asset: AssetLoad | undefined, data: DataItem<DataType>): DataItem<DataType> {
	const store = asset?.asLandingPage();
	if (!store) {
		return data;
	}
	const landingPage = get(store);
	const landingPageDataItem = {
		get primaryText() {
			return landingPage.primaryText;
		},
		get secondaryText() {
			return landingPage.secondaryText ?? create(DetailItemSchema);
		},
		get tertiaryText() {
			return landingPage.tertiaryText;
		},
		get ctaText() {
			return landingPage.ctaText;
		},
		get images() {
			return landingPage.images;
		},
		get supportingText() {
			return landingPage.supportingText;
		},
		get highlightText() {
			return landingPage.highlightText;
		},

		// currently not configurable in landing page message type
		get successHeader() {
			return data.successHeader;
		},
		get successText() {
			return data.successText;
		},
		get canConvert() {
			return data.canConvert;
		},
		get value() {
			return data.value;
		},
		suiteId: suite.id,
	};

	return landingPageDataItem;
}

function createExternalDataStore(suite: Suite, asset?: AssetLoad): DataItem<undefined> {
	const store = asset?.asExternalDestination();
	return {
		get primaryText() {
			if (!store) {
				return "";
			}
			const external = get(store);
			if (external?.url && !external?.url.startsWith("{{")) {
				return new URL(external.url).hostname.replace("www.", "");
			}
			return "";
		},
		secondaryText: undefined,
		tertiaryText: [],
		images: [],
		successHeader: "",
		successText: "",
		ctaText: "Visit",
		canConvert: true,
		suiteId: suite.id,
		value: undefined,
	};
}

function createCustomDataStore(suite: Suite, asset?: AssetLoad): DataItem<undefined> {
	return {
		primaryText: "",
		secondaryText: undefined,
		tertiaryText: [],
		images: [],
		successHeader: "",
		successText: "",
		ctaText: actionLabels.get(suite.assets[asset?.key() ?? ""]?.conversionType),
		canConvert: true,
		suiteId: suite.id,
		value: undefined,
	};
}
